import { suppressExternalServices, apiRoot } from '../../data/constants.ts';
import { getAppConfig, type HubSpotConfig } from '../as-app/app-config.ts';

function buildHubspotSrcdoc(config: HubSpotConfig): string {
  const { region, portalId, embedId, elementId } = config;
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; overflow: hidden; }
  body { font-family: sans-serif; }
</style>
</head>
<body>
<div id="${elementId}"></div>
<script>
  var s = document.createElement('script');
  s.charset = 'utf-8';
  s.src = 'https://js.hubspot.com/content-embed/v1.js';
  s.onerror = function() {
    parent.postMessage({ type: 'hs-embed-blocked' }, '*');
  };
  s.onload = function() {
    try {
      hbspt.content.create({
        region: "${region}",
        portalId: "${portalId}",
        embedId: "${embedId}",
        elementId: "${elementId}"
      });
      new ResizeObserver(function() {
        parent.postMessage({ type: 'hs-embed-resize', height: document.body.scrollHeight }, '*');
      }).observe(document.body);
    } catch(e) {
      parent.postMessage({ type: 'hs-embed-blocked' }, '*');
    }
  };
  document.head.appendChild(s);
</` + `script>
</body>
</html>`;
}

function stripTracking(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, noscript').forEach(el => el.remove());
  doc.querySelectorAll('img').forEach(el => {
    const w = parseInt(el.getAttribute('width') ?? '9999', 10);
    const h = parseInt(el.getAttribute('height') ?? '9999', 10);
    if (w <= 1 && h <= 1) el.remove();
  });
  const headStyles = Array.from(doc.head.querySelectorAll('style')).map(s => s.outerHTML).join('');
  return headStyles + doc.body.innerHTML;
}

export class AsEmbedContent extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(/*css*/`
      :host { display: block; width: 100%; }
      .blocked { display: none; padding: 12px; color: #888; font-size: 0.85em; font-style: italic; }
      .blocked.visible { display: block; }
      .embed-content { 
        padding: 0; display: flow-root; overflow: hidden; 
        &  img, .embed-content video { max-width: 100%; height: auto; display: block; }
        & table { max-width: 100%; }
        & .embed-rich-text { 
          margin-top:0; 
          padding:1rem;

        }
        & p {
          padding: 1rem; 
          background:white; 
          border-radius:1rem; 
          box-shadow:var(--shadow-high);
          margin:0;
        }
        & h1 {
          font-style:italic; 
          margin: 0;
          padding: 0.25rem .5rem 1rem;
        }
      }
    `);
    return sheet;
  })();

  private iframe: HTMLIFrameElement | null = null;
  private messageHandler: ((e: MessageEvent) => void) | null = null;
  private _lastRequestId = 0;
  private _itemCount = 0;
  private _initPending = false;

  static get observedAttributes(): string[] {
    return ['embed-module-key', 'index'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsEmbedContent.sheet];
  }

  connectedCallback(): void {
    this.scheduleInit();
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string): void {
    if (name === 'embed-module-key' && oldVal !== newVal) {
      this.isConnected ? this.scheduleInit() : void 0;
    } else if (name === 'index' && oldVal !== newVal) {
      this.updateVisibleIndex();
    }
  }

  private scheduleInit(): void {
    if (this._initPending) return;
    this._initPending = true;
    Promise.resolve().then(() => {
      this._initPending = false;
      if (this.isConnected) this.init();
    });
  }

  private updateVisibleIndex(): void {
    const index = parseInt(this.getAttribute('index') || '0', 10);
    this.shadowRoot!.querySelectorAll('.embed-content').forEach((el, i) => {
      (el as HTMLElement).style.display = i === index ? 'block' : 'none';
    });
  }

  disconnectedCallback(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }

  private clearContent(): void {
    this.shadowRoot!.querySelectorAll('iframe, .embed-content, .blocked').forEach(el => el.remove());
    this._itemCount = 0;
  }

  private async init(): Promise<void> {
    const key = this.getAttribute('embed-module-key');
    if (!key) return;

    const requestId = ++this._lastRequestId;
    this.clearContent();

    if (!suppressExternalServices) {
      await this.initFromApi(key, requestId);
    } else {
      await this.initHubspot(key, requestId);
    }
  }

  private async initFromApi(key: string, requestId: number): Promise<void> {
    try {
      const url = new URL(apiRoot, window.location.origin);
      url.searchParams.set('function', 'getEmbeds');
      url.searchParams.set('location', key);

      const response = await fetch(url.toString());
      if (requestId !== this._lastRequestId) return;
      if (!response.ok) return;

      const data = await response.json();
      if (!data?.success) return;

      const rawData = data.data;
      const items = Array.isArray(rawData) ? rawData : [rawData];
      this._itemCount = 0;

      items.forEach((item, i) => {
        const html = typeof item === 'string' ? item : (item?.html ?? '');
        if (!html) return;

        const container = document.createElement('div');
        container.className = 'embed-content';
        container.innerHTML = html;

        // If index is specified, hide all but the requested one. 
        // Otherwise, show all (default for banner/simple usage).
        if (this.hasAttribute('index')) {
          const targetIndex = parseInt(this.getAttribute('index') || '0', 10);
          container.style.display = i === targetIndex ? 'block' : 'none';
        }

        this.shadowRoot!.appendChild(container);
        this._itemCount++;
      });

      if (this._itemCount > 0) {
        this.dispatchEvent(new CustomEvent('as-embed-ready', {
          detail: { count: this._itemCount, location: key },
          bubbles: true,
          composed: true
        }));
        this.dispatchEvent(new CustomEvent('embed-load', { bubbles: false }));
      }
    } catch (err) {
      console.error('Failed to fetch API embed:', err);
    }
  }

  private async initHubspot(key: string, requestId: number): Promise<void> {
    const asApp = this.closest('as-app');
    const appId = asApp?.getAttribute('app-id') ?? '';
    const settings = await getAppConfig(appId);
    if (requestId !== this._lastRequestId) return;

    const config = settings.hubspot?.[key];
    if (!config) return;

    // Try fetching the pre-rendered embed HTML directly and strip tracking noise.
    const directUrl = `https://${config.portalId}.hs-sites-${config.region}.com/_hcms/embed/${config.embedId}`;
    try {
      const res = await fetch(directUrl, { mode: 'cors' });
      if (requestId !== this._lastRequestId) return;
      if (res.ok) {
        const html = stripTracking(await res.text());
        const container = document.createElement('div');
        container.className = 'embed-content';
        container.innerHTML = html;
        this.shadowRoot!.appendChild(container);
        this.dispatchEvent(new CustomEvent('embed-load', { bubbles: false }));
        return;
      }
    } catch {
      // CORS or network error — fall through to iframe SDK
    }

    if (requestId !== this._lastRequestId) return;

    // Fallback: load HubSpot SDK inside a sandboxed srcdoc iframe.
    const blockedMsg = document.createElement('div');
    blockedMsg.className = 'blocked';
    blockedMsg.textContent = 'Content unavailable — do you have an ad blocker active?';
    this.shadowRoot!.appendChild(blockedMsg);

    this.iframe = document.createElement('iframe');
    this.iframe.srcdoc = buildHubspotSrcdoc(config);
    this.iframe.style.cssText = 'width:100%;height:0;border:none;display:block;';
    this.iframe.setAttribute('title', 'Welcome');
    this.shadowRoot!.appendChild(this.iframe);

    this.messageHandler = (e: MessageEvent) => {
      if (e.source !== this.iframe?.contentWindow) return;
      if (e.data?.type === 'hs-embed-resize' && typeof e.data.height === 'number') {
        this.iframe!.style.height = e.data.height + 'px';
      } else if (e.data?.type === 'hs-embed-blocked') {
        this.iframe!.remove();
        this.iframe = null;
        (this.shadowRoot!.querySelector('.blocked') as HTMLElement | null)?.classList.add('visible');
      }
    };
    window.addEventListener('message', this.messageHandler);

    this.iframe.addEventListener('load', () => {
      this.dispatchEvent(new CustomEvent('embed-load', { bubbles: false }));
    }, { once: true });
  }
}

if (!customElements.get('as-embed-content')) {
  customElements.define('as-embed-content', AsEmbedContent);
} else {
  console.warn('as-embed-content already defined');
}
